import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { DisplayCreative } from '@feathr/blackbox';
import { FormSummaryItem, Skeleton } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import type { ListResponse } from '@feathr/rachis';

import type { ISummaryProps } from './CampaignSummary.types';

import * as styles from './CreativesSummary.css';

const CreativesSummary = observer(
  ({ campaign, validationErrors, creatives: localCreatives }: ISummaryProps): JSX.Element => {
    const { t } = useTranslation();
    const { Creatives } = useStore();

    // If local creatives are provided (from review step), use those (for displaying ephemeral creatives)
    // Otherwise, get creatives from API (for campaign summaries/reports)
    const apiCreatives = localCreatives
      ? null
      : (Creatives.list({
          filters: {
            _parent: campaign.id,
            is_archived__ne: true,
          },
          pagination: {
            page_size: 1000,
          },
        }) as ListResponse<DisplayCreative>);

    // Use local creatives if available (includes ephemeral), otherwise use API creatives
    const creativesToShow = localCreatives ?? apiCreatives?.models ?? [];
    const isPending = localCreatives ? false : apiCreatives?.isPending;

    const filteredCreatives = creativesToShow.filter((c) => !c.get('is_archived'));

    return (
      <FormSummaryItem
        errors={validationErrors?.redirects}
        label={t('Creatives')}
        value={
          isPending || filteredCreatives.length > 0 ? (
            <ol className={classNames({ [styles.list]: filteredCreatives.length > 100 })}>
              {isPending && (
                <li>
                  <Skeleton width={100} />
                </li>
              )}
              {filteredCreatives.map((crv) => {
                const spec = crv.getSpec();
                return (
                  <li key={crv.id}>
                    {crv.get('name')} | {crv.get('width')}px x {crv.get('height')}px |{' '}
                    {spec ? spec.name : t('unknown')}
                  </li>
                );
              })}
            </ol>
          ) : (
            t('(None)')
          )
        }
      />
    );
  },
);

CreativesSummary.displayName = 'CreativesSummary';

export default CreativesSummary;
